import type { Dispatch, SetStateAction } from 'react';
import { Button, Inline, Stack } from '@etg/wings';
import { useTranslation } from '@eti/providers';
import { css } from '@eti/styles';
import { LoadingDots } from '@eti/components';
import { useScrollTo } from '@eti/utils';
import type { RebookingTripsType } from '../models';
import NoResultsContent from '../../result/components/v3/NoResultsContent';

const stackStyles = css`
  margin-bottom: 32px;
`;

const customStyles = css`
  min-width: 135px;
`;

export interface NoResultsProps {
  flightResults: RebookingTripsType;
  flightsCount: number;
  setFetchMoreResults: Dispatch<SetStateAction<boolean>>;
  shouldFetchMoreResults: boolean;
}

const NoResults = ({
  flightResults,
  flightsCount,
  setFetchMoreResults,
  shouldFetchMoreResults,
}: NoResultsProps) => {
  const { t } = useTranslation();
  const { scrollVertical } = useScrollTo();
  const hasNoFlights = flightsCount === 0;
  const hasNoMoreFlights = flightsCount !== 0 && flightsCount === flightResults?.length;
  const scrollToTop = () => scrollVertical('smooth', 0);

  const shouldShowLoadMoreButton = !hasNoMoreFlights && !hasNoFlights;

  return (
    <Stack align="center" className={stackStyles} spacing={16}>
      {hasNoFlights && (
        <NoResultsContent
          dataTestId="changeTrip-hasNoFlights-button"
          description={t('ChangeTrip.NoResults.Description.Text')}
          title={t('ChangeTrip.NoResults.Title')}
        />
      )}
      {hasNoMoreFlights && (
        <NoResultsContent
          buttonText={t('ChangeTrip.BackToTop.Button.Label')}
          dataTestId="changeTrip-hasNoMoreFlights-button"
          description={t(
            'ChangeTrip.ScrollNoMoreResults.Label',
            flightResults?.length,
            flightResults?.length,
          )}
          onClick={scrollToTop}
          title={t('ChangeTrip.ScrollNoMoreResults.Title')}
        />
      )}
      {shouldShowLoadMoreButton && (
        <Inline align="center">
          <Button
            className={customStyles}
            data-testid="changeTrip-loadMore-button"
            onClick={() => setFetchMoreResults(true)}
            variant="secondary"
          >
            {shouldFetchMoreResults ? (
              <LoadingDots dataTestId="changeTrip-loading-dots" />
            ) : (
              t('ChangeTrip.LoadMore.Button.Label')
            )}
          </Button>
        </Inline>
      )}
    </Stack>
  );
};

export default NoResults;
